import { CreateTeamMemberInputSchema } from "entities/team-members/types/CreateTeamMemberInput"
import { TeamRole } from "entities/team-members/types/TeamRole"
import I18n from "i18n"
import z from "zod"

// Helper functions for email validation
function hasEmailContent(email?: string): boolean {
  return !!(email && email.trim() !== "")
}

function normalizeEmail(email: string): string {
  return email.toLowerCase().trim()
}

function areEmailsEqual(email1?: string, email2?: string): boolean {
  if (!email1 || !email2) return false
  return normalizeEmail(email1) === normalizeEmail(email2)
}

export const OptionalCreateTeamMemberInputSchema = z.object({
  email: z
    .email({ error: I18n.t("live:ValidationError.email") })
    .or(z.literal("")),
  role: z.enum(TeamRole).optional()
})

// Dynamic validation function for multiple team members
export function createMultipleTeamMembersFormSchema(
  currentUserEmail?: string,
  existingTeamMemberEmails?: string[]
) {
  return z.object({
    teamMembers: z
      .array(OptionalCreateTeamMemberInputSchema)
      .superRefine((val, ctx) => {
        const hasEmail = val.some(item => hasEmailContent(item.email))

        if (!hasEmail) {
          ctx.addIssue({
            code: "custom",
            message: I18n.t("live:Validation.objectAnyOf"),
            path: [0, "email"] // force error to email field first element
          })
        }

        // Validate each email for current user and existing members
        val.forEach((member, index) => {
          if (hasEmailContent(member.email)) {
            const email = normalizeEmail(member.email as string)

            // Check if user is trying to invite themselves
            if (currentUserEmail && areEmailsEqual(email, currentUserEmail)) {
              ctx.addIssue({
                code: "custom",
                message: I18n.t("live:ValidationError.emailCurrentUser"),
                path: [index, "email"]
              })
            }

            // Check if email is already a team member
            if (
              existingTeamMemberEmails &&
              existingTeamMemberEmails.some(existingEmail =>
                areEmailsEqual(existingEmail, email)
              )
            ) {
              ctx.addIssue({
                code: "custom",
                message: I18n.t("live:ValidationError.emailAlreadyExists"),
                path: [index, "email"]
              })
            }

            // Check for duplicate emails within the form
            const duplicateIndex = val.findIndex(
              (otherMember, otherIndex) =>
                otherIndex !== index &&
                hasEmailContent(otherMember.email) &&
                areEmailsEqual(otherMember.email, email)
            )

            if (duplicateIndex !== -1) {
              ctx.addIssue({
                code: "custom",
                message: I18n.t("live:ValidationError.emailDuplicate"),
                path: [index, "email"]
              })
            }
          }
        })
      })
  })
}

// Dynamic validation function for single team member
export function createSingleTeamMemberFormSchema(
  currentUserEmail?: string,
  existingTeamMemberEmails?: string[]
) {
  return z.object({
    teamMembers: z
      .array(CreateTeamMemberInputSchema)
      .length(1)
      .superRefine((val, ctx) => {
        // Validate the single team member
        val.forEach((member, index) => {
          if (member.email) {
            const email = normalizeEmail(member.email)

            // Check if user is trying to invite themselves
            if (currentUserEmail && areEmailsEqual(email, currentUserEmail)) {
              ctx.addIssue({
                code: "custom",
                message: I18n.t("live:ValidationError.emailCurrentUser"),
                path: [index, "email"]
              })
            }

            // Check if email is already a team member
            if (
              existingTeamMemberEmails &&
              existingTeamMemberEmails.some(existingEmail =>
                areEmailsEqual(existingEmail, email)
              )
            ) {
              ctx.addIssue({
                code: "custom",
                message: I18n.t("live:ValidationError.emailAlreadyExists"),
                path: [index, "email"]
              })
            }

            // Check for duplicate emails within the form (for consistency, though unlikely with single member)
            const duplicateIndex = val.findIndex(
              (otherMember, otherIndex) =>
                otherIndex !== index &&
                otherMember.email &&
                areEmailsEqual(otherMember.email, email)
            )

            if (duplicateIndex !== -1) {
              ctx.addIssue({
                code: "custom",
                message: I18n.t("live:ValidationError.emailDuplicate"),
                path: [index, "email"]
              })
            }
          }
        })
      })
  })
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- only referenced through `typeof` below, which typescript-eslint 8 counts as unused
const CreateMultipleTeamMembersFormSchema =
  createMultipleTeamMembersFormSchema()

export type CreateTeamMemberFormData = z.infer<
  typeof CreateMultipleTeamMembersFormSchema
>
